// Ecosystem Edge §09 — public share CTA for a completed goal's
// Verified Return Certificate. Rendered only alongside a goal that is
// already completed (the certificate exists). Minting is an explicit
// user action: it calls get_or_create_sika_certificate_share, which
// itself refuses any non-completed/unowned goal (no fabrication), then
// opens the public, non-PII SVG card in a new tab. Errors are surfaced
// honestly — never a fabricated link.

import { useCallback } from "react";
import { useSikaCertificateShare } from "../../sika/useSikaCertificateShare.ts";
import { openShareCertificate } from "../../sika/shareCertificateEndpoint.ts";

export interface CertificateShareButtonProps {
  goalId: string;
}

export function CertificateShareButton({
  goalId,
}: CertificateShareButtonProps): JSX.Element {
  const { mint, loading, error } = useSikaCertificateShare();

  const onShare = useCallback(async () => {
    const token = await mint(goalId);
    if (token) openShareCertificate(token);
  }, [mint, goalId]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
      data-testid="cert-share"
    >
      <button
        type="button"
        onClick={() => void onShare()}
        disabled={loading}
        data-testid="cert-share-cta"
        style={{
          alignSelf: "flex-start",
          padding: "6px 12px",
          background: "transparent",
          color: "var(--sa-accent-saffron)",
          border: "1px solid var(--sa-accent-saffron)",
          borderRadius: 3,
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          cursor: loading ? "default" : "pointer",
          opacity: loading ? 0.6 : 1,
        }}
      >
        {loading ? "Preparing…" : "Share verified certificate"}
      </button>
      {error ? (
        <div
          role="status"
          data-testid="cert-share-error"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10.5,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.04em",
          }}
        >
          Certificate not shareable yet: {error}
        </div>
      ) : null}
    </div>
  );
}
